<template>
  <div class="view-invoice-modal">
    <q-linear-progress v-if="loading" indeterminate />
    
    <div v-else>
      <!-- Информация о накладной (режим просмотра) -->
      <div v-if="!editMode" class="invoice-info">
        <div class="info-row">
          <span class="label">Номер:</span>
          <strong>{{ invoice?.num || invoice?.invoice_num }}</strong>
        </div>
        <div class="info-row">
          <span class="label">Дата:</span>
          <span>{{ invoice?.date || formatDate(invoice?.invoice_date) }}</span>
        </div>
        <div class="info-row">
          <span class="label">Сумма:</span>
          <strong class="amount">{{ formatAmount(invoice?.invoice_sum || invoice?.calculated_sum) }}</strong>
        </div>
        <div class="info-row">
          <span class="label">НДС:</span>
          <q-badge :color="invoice?.is_nds ? 'green' : 'grey'" :label="invoice?.is_nds ? 'Да' : 'Нет'" />
        </div>
        <div class="info-row" v-if="invoice?.employee_name">
          <span class="label">Создал:</span>
          <span>{{ invoice?.employee_name }}</span>
        </div>
        <div class="info-row" v-if="data.isShowAll">
          <span class="label">ID:</span>
          <span>{{ invoice?.provider_request_invoice_id }}</span>
        </div>
        <template v-if="data.isShowAll">
          <div class="info-row info-row--onec">
            <span class="label">1С:</span>
            <div class="onec-line">
              <q-badge
                :color="getSendTo1cBadgeColor(invoice?.is_send_to_1c)"
                :label="getSendTo1cLabel(invoice?.is_send_to_1c)"
              />
              <span
                v-if="Number(invoice?.is_send_to_1c) === 2 && invoice?.kod_bk"
                class="onec-kod"
              >
                Код из 1С: <strong>{{ invoice.kod_bk }}</strong>
              </span>
            </div>
          </div>
        </template>
      </div>

      <!-- Форма редактирования -->
      <div v-else class="invoice-edit-form">
        <div class="form-row">
          <div class="form-field">
            <div class="field-label">Номер накладной *</div>
            <q-input
              v-model="editForm.invoice_num"
              outlined
              dense
              placeholder="Введите номер"
            />
          </div>
          <div class="form-field">
            <div class="field-label">Дата накладной *</div>
            <q-input
              v-model="editForm.invoice_date"
              type="date"
              outlined
              dense
            />
          </div>
          <div class="form-field">
            <div class="field-label">С НДС</div>
            <q-toggle v-model="editForm.is_nds" />
          </div>
        </div>
      </div>

      <!-- Доверенность (просмотр + редактирование) -->
      <div class="poa-section">
        <q-expansion-item
          v-model="poaExpanded"
          dense
          icon="assignment"
          label="Доверенность"
          caption="Данные доверенности"
          class="poa-expansion"
        >
          <div class="poa-fields">
            <template v-if="!editMode">
              <div class="poa-view">
                <div class="poa-view-row">
                  <span class="poa-label">Номер</span>
                  <span class="poa-value">{{ invoice?.power_of_attorney_num || 'не указан' }}</span>
                </div>
                <div class="poa-view-row">
                  <span class="poa-label">Дата</span>
                  <span class="poa-value">{{ invoice?.power_of_attorney_date ? formatDate(invoice?.power_of_attorney_date) : 'не указана' }}</span>
                </div>
                <div class="poa-view-row">
                  <span class="poa-label">Кому выдана</span>
                  <span class="poa-value">{{ invoice?.power_of_attorney_issued_to || 'не указано' }}</span>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="poa-row">
                <div class="poa-field poa-field--num">
                  <div class="field-label">Номер доверенности</div>
                  <q-input
                    v-model="editForm.power_of_attorney_num"
                    outlined
                    dense
                    placeholder="Напр. 123/А"
                  />
                </div>
                <div class="poa-field poa-field--date">
                  <div class="field-label">Дата доверенности</div>
                  <q-input
                    v-model="editForm.power_of_attorney_date"
                    type="date"
                    outlined
                    dense
                  />
                </div>
              </div>
              <div class="poa-field">
                <div class="field-label">Кому выдана доверенность</div>
                <q-input
                  v-model="editForm.power_of_attorney_issued_to"
                  outlined
                  dense
                  placeholder="ФИО"
                />
              </div>
            </template>
          </div>
        </q-expansion-item>
      </div>

      <!-- Баннер: накладная на утверждении -->
      <div v-if="isPendingApproval" class="approval-banner">
        <q-icon name="hourglass_empty" size="20px" color="amber-9" />
        <span>Накладная на утверждении. Действия временно недоступны.</span>
      </div>

      <!-- Баннер: цена согласована -->
      <div v-if="isApprovedApproval" class="approved-banner">
        <q-icon name="check_circle" size="20px" color="positive" class="approved-banner__icon" />
        <div class="approved-banner__body">
          <span>Утверждение цен согласовано.</span>
          <div v-if="invoice?.approval_approve_employee_name" class="approved-banner__meta">
            <span class="approved-banner__who">{{ invoice.approval_approve_employee_name }}</span>
            <span v-if="invoice?.approval_approve_comment" class="approved-banner__comment">{{ invoice.approval_approve_comment }}</span>
          </div>
        </div>
      </div>

      <!-- Баннер: цена отклонена -->
      <div v-if="isRejectedApproval" class="rejected-banner">
        <q-icon name="block" size="20px" color="negative" class="rejected-banner__icon" />
        <div class="rejected-banner__body">
          <span>Утверждение цен отклонено. Исправьте цену или количество в режиме редактирования накладной либо удалите накладную и создайте заново.</span>
          <div v-if="invoice?.approval_reject_employee_name" class="rejected-banner__meta">
            <span class="rejected-banner__who">{{ invoice.approval_reject_employee_name }}</span>
            <span v-if="invoice?.approval_reject_comment" class="rejected-banner__comment">{{ invoice.approval_reject_comment }}</span>
          </div>
        </div>
      </div>

      <!-- Баннер для вручную загруженной накладной -->
      <div v-if="isManualSigned" class="manual-banner">
        <q-icon name="upload_file" size="20px" color="amber-9" />
        <span>Вручную загруженная накладная</span>
      </div>

      <!-- Блок подписей -->
      <div v-if="!isManualSigned && invoice?.sign_info" class="sign-info-section">
        <div class="sign-header">
          <span class="sign-title">Подписание</span>
          <q-badge :color="getSignStatusColor(invoice.sign_info)" :label="getSignStatusLabel(invoice.sign_info)" />
          <span v-if="invoice.sign_info.sign_total" class="sign-progress">
            {{ invoice.sign_info.sign_signed || 0 }} / {{ invoice.sign_info.sign_total }}
          </span>
        </div>
        <q-linear-progress
          v-if="invoice.sign_info.progress_percent != null"
          :value="invoice.sign_info.progress_percent / 100"
          :color="getSignStatusColor(invoice.sign_info)"
          class="sign-progress-bar"
        />
        <div v-if="invoice.sign_info.signatures?.length" class="signatures-list">
          <div 
            v-for="(sig, idx) in invoice.sign_info.signatures" 
            :key="idx"
            class="signature-item"
          >
            <q-icon 
              :name="sig.status === 'SIGNED' ? 'check_circle' : 'radio_button_unchecked'" 
              :color="sig.status === 'SIGNED' ? 'green' : 'grey'" 
              size="18px"
            />
            <div class="signature-info">
              <span class="signature-desc">{{ sig.description }}</span>
              <span v-if="sig.signed_by?.dn" class="signature-signer">{{ sig.signed_by.dn }}</span>
              <span v-if="sig.signed_at" class="signature-date">{{ formatDateTime(sig.signed_at) }}</span>
            </div>
            <q-badge 
              :color="getSignatureStatusColor(sig.status)" 
              :label="getSignatureStatusLabel(sig.status)" 
              dense
            />
          </div>
        </div>
        <div v-if="invoice.sign_info.next_to_sign" class="next-to-sign">
          <q-icon name="arrow_forward" size="14px" color="blue" />
          <span>Следующий: {{ invoice.sign_info.next_to_sign.description }}</span>
        </div>
      </div>

      <!-- Позиции накладной -->
      <div class="items-section">
        <h4>Позиции накладной {{ editMode ? '(редактирование)' : '' }}</h4>
        
        <q-table
          v-if="items.length"
          :rows="tableRows"
          :columns="editMode ? editColumns : columns"
          row-key="_rowKey"
          flat
          dense
          :pagination="{ rowsPerPage: 0 }"
          hide-pagination
          class="items-table"
        >
          <template v-slot:body="props">
            <!-- Строка-разделитель группы (заявка) -->
            <q-tr v-if="props.row._isGroupHeader" class="group-header-row">
              <q-td :colspan="(editMode ? editColumns : columns).length" class="group-header-cell">
                <div class="group-header-content">
                  <q-icon name="description" size="18px" color="primary" class="q-mr-sm" />
                  <strong>Заявка #{{ props.row._requestId }}</strong>
                  <q-badge color="blue-grey-4" class="q-ml-sm">
                    {{ props.row._itemsCount }} позиц.
                  </q-badge>
                </div>
              </q-td>
            </q-tr>
            <!-- Обычная строка -->
            <q-tr v-else :props="props">
              <q-td class="material-cell">
                <div class="material-info" :title="props.row.material_name">
                  <div class="material-title">{{ props.row.material_name }}</div>
                  <div class="provider-meta">
                    <div class="provider-block" :class="{ 'provider-block--identical': isProviderNameIdentical(props.row) }">
                      <div class="provider-head">
                        <span class="provider-label">Название поставщика</span>
                        <q-badge
                          v-if="isProviderNameIdentical(props.row)"
                          color="green-7"
                          text-color="white"
                          class="provider-badge"
                          label="Идентично"
                        />
                      </div>
                      <div v-if="!isProviderNameIdentical(props.row)" class="provider-value provider-value--provider">
                        {{ props.row.provider_material_name || 'не указано' }}
                      </div>
                    </div>
                    <div class="provider-block">
                      <div class="provider-head">
                        <span class="provider-label">Номенклатурный номер поставщика</span>
                      </div>
                      <div class="provider-value">
                        {{ props.row.provider_nomenklatur_num || 'не указан' }}
                      </div>
                    </div>
                  </div>
                </div>
              </q-td>
              <q-td class="text-center edit-qty-cell">
                <template v-if="editMode">
                  <div class="edit-input-wrap">
                    <q-input
                      v-model.number="editItems[props.row.provider_request_invoice_item_id]"
                      type="number"
                      dense
                      outlined
                      :min="0.01"
                      :max="props.row.max_qty"
                      style="width: 90px"
                      :hint="'макс: ' + props.row.max_qty"
                      @update:model-value="recalcItemSum(props.row)"
                    />
                    <span class="unit-name unit-name--edit">{{ props.row.material_unit_name }}</span>
                  </div>
                </template>
                <template v-else>
                  {{ props.row.material_cnt }}
                  <span class="unit-name">{{ props.row.material_unit_name }}</span>
                </template>
              </q-td>
              <q-td class="text-right edit-price-cell">
                <template v-if="editMode">
                  <q-input
                    v-model.number="editPrices[props.row.provider_request_invoice_item_id]"
                    type="number"
                    dense
                    outlined
                    :min="0"
                    style="width: 120px"
                    hint=" "
                    @update:model-value="recalcItemSum(props.row)"
                  />
                </template>
                <template v-else>
                  {{ formatAmount(props.row.material_price) }}
                </template>
              </q-td>
              <q-td class="text-right">
                <strong>{{ formatAmount(editMode ? getEditItemSum(props.row) : props.row.item_sum) }}</strong>
              </q-td>
              <q-td v-if="editMode" class="text-center">
                <q-btn
                  flat dense round
                  icon="delete"
                  color="red"
                  size="sm"
                  :disable="items.length <= 1"
                  :title="items.length <= 1 ? 'Нельзя удалить последнюю позицию' : 'Удалить позицию'"
                  @click="confirmDeleteItem(props.row)"
                />
              </q-td>
            </q-tr>
          </template>
        </q-table>

        <div v-else class="no-items">
          Нет позиций
        </div>
      </div>

      <!-- Добавление позиций в режиме редактирования -->
      <div v-if="editMode && availableItems.length > 0" class="add-items-section">
        <div class="add-items-header">
          <h4>Добавить позиции</h4>
          <q-badge color="blue" :label="availableItems.length + ' доступно'" />
        </div>
        <q-table
          :rows="availableItems"
          :columns="addItemColumns"
          row-key="provider_request_item_id"
          flat
          dense
          :pagination="{ rowsPerPage: 5 }"
          class="add-items-table"
        >
          <template v-slot:body-cell-material_name="props">
            <q-td :props="props">
              <div class="material-info" :title="props.row.material_name">
                {{ props.row.material_name }}
              </div>
            </q-td>
          </template>
          <template v-slot:body-cell-remaining_cnt="props">
            <q-td :props="props" class="text-center">
              <span class="remaining">{{ props.row.remaining_cnt }}</span>
              <span class="unit-name">{{ props.row.material_unit_name }}</span>
            </q-td>
          </template>
          <template v-slot:body-cell-add_price="props">
            <q-td :props="props" class="text-right">
              <q-input
                v-model.number="addItemPrice[props.row.provider_request_item_id]"
                type="number"
                dense
                outlined
                :min="0.01"
                style="width: 110px"
                placeholder="Цена"
                :error="!(addItemPrice[props.row.provider_request_item_id] > 0) && addItemQty[props.row.provider_request_item_id] > 0"
                hint=" "
              />
            </q-td>
          </template>
          <template v-slot:body-cell-add_qty="props">
            <q-td :props="props" class="text-center">
              <div class="edit-input-wrap">
                <q-input
                  v-model.number="addItemQty[props.row.provider_request_item_id]"
                  type="number"
                  dense
                  outlined
                  :min="0.01"
                  :max="props.row.remaining_cnt"
                  style="width: 80px"
                  placeholder="Кол-во"
                  hint=" "
                />
                <span class="unit-name">{{ props.row.material_unit_name }}</span>
              </div>
            </q-td>
          </template>
          <template v-slot:body-cell-actions="props">
            <q-td :props="props" class="text-center">
              <q-btn
                flat
                dense
                round
                icon="add"
                color="green"
                size="sm"
                :disable="!(addItemQty[props.row.provider_request_item_id] > 0) || !(addItemPrice[props.row.provider_request_item_id] > 0)"
                title="Добавить в накладную"
                @click="doAddItem(props.row)"
              />
            </q-td>
          </template>
        </q-table>
      </div>

      <!-- Итого -->
      <div class="totals-section">
        <div class="total-row">
          <span>Всего позиций:</span>
          <strong>{{ items.length }}</strong>
        </div>
        <div class="total-row">
          <span>Общая сумма:</span>
          <strong class="total-sum">{{ formatAmount(totalSum) }}</strong>
        </div>
      </div>
    </div>

    <q-card-actions align="right" class="q-mt-md q-gutter-sm">
      <!-- Режим редактирования -->
      <template v-if="editMode && showActions">
        <q-btn
          flat
          label="Отмена"
          color="grey"
          @click="cancelEdit"
        />
        <q-btn
          unelevated
          label="Сохранить"
          color="primary"
          icon="save"
          :loading="saving"
          :disable="!canSaveEdit"
          @click="saveEdit"
        />
      </template>
      <!-- Режим просмотра -->
      <template v-else>
        <q-btn
          v-if="canDelete && showActions"
          round
          color="red"
          icon="delete"
          :loading="deleting"
          :disable="loading || deleting"
          @click="confirmDelete"
        >
          <q-tooltip>Удалить накладную</q-tooltip>
        </q-btn>
        <q-btn
          v-if="canEdit && showActions"
          round
          color="orange"
          icon="edit"
          :disable="loading"
          @click="startEdit"
        >
          <q-tooltip>Редактировать</q-tooltip>
        </q-btn>
        <q-btn
          v-if="canSign && showActions"
          round
          color="green"
          icon="verified"
          :loading="signing"
          :disable="loading || signing"
          @click="signInvoice"
        >
          <q-tooltip>Подписать</q-tooltip>
        </q-btn>
        <q-btn
          v-if="isManualSigned"
          round
          color="amber-9"
          icon="cloud_download"
          :loading="downloading"
          :disable="loading || downloading"
          @click="downloadInvoiceScanFile"
        >
          <q-tooltip>Скачать загруженный скан</q-tooltip>
        </q-btn>
        <q-btn
          v-else
          round
          color="primary"
          icon="download"
          :loading="downloading"
          :disable="loading || downloading"
          @click="downloadInvoicePdf"
        >
          <q-tooltip>Скачать накладную</q-tooltip>
        </q-btn>
        <q-btn flat label="Закрыть" color="grey" @click="closeModal()" />
      </template>
    </q-card-actions>
  </div>
</template>

<script>
import { downloadInvoice, downloadInvoiceScan, getInvoice, getInvoiceItems, signInvoice, deleteInvoice, updateInvoice, deleteInvoiceItem, updateInvoiceItemQty, addInvoiceItem, getAvailableItemsForInvoice } from "@/views/SupplierRequests/services";

export default {
  name: "ViewInvoice",
  props: {
    data: {
      type: Object,
      required: true,
    },
    closeModal: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      downloading: false,
      signing: false,
      deleting: false,
      saving: false,
      editMode: false,
      poaExpanded: false,
      invoiceData: null,
      items: [],
      editItems: {}, // { provider_request_invoice_item_id: material_cnt }
      editPrices: {}, // { provider_request_invoice_item_id: material_price }
      editForm: {
        invoice_num: '',
        invoice_date: '',
        is_nds: true,
        power_of_attorney_num: '',
        power_of_attorney_date: null,
        power_of_attorney_issued_to: '',
      },
      columns: [
        { name: 'material_name', label: 'Материал', field: 'material_name', align: 'left' },
        { name: 'material_cnt', label: 'Кол-во', field: 'material_cnt', align: 'center' },
        { name: 'material_price', label: 'Цена', field: 'material_price', align: 'right' },
        { name: 'item_sum', label: 'Сумма', field: 'item_sum', align: 'right' },
      ],
      editColumns: [
        { name: 'material_name', label: 'Материал', field: 'material_name', align: 'left' },
        { name: 'material_cnt', label: 'Кол-во', field: 'material_cnt', align: 'center', style: 'width: 120px' },
        { name: 'material_price', label: 'Цена', field: 'material_price', align: 'right', style: 'width: 140px' },
        { name: 'item_sum', label: 'Сумма', field: 'item_sum', align: 'right' },
        { name: 'actions', label: '', align: 'center', style: 'width: 50px' },
      ],
      // Для добавления позиций
      availableItems: [],
      addItemQty: {},    // { provider_request_item_id: qty }
      addItemPrice: {},  // { provider_request_item_id: price }
      addItemColumns: [
        { name: 'material_name', label: 'Материал', field: 'material_name', align: 'left' },
        { name: 'remaining_cnt', label: 'Остаток', field: 'remaining_cnt', align: 'center', style: 'width: 100px' },
        { name: 'add_price', label: 'Цена', align: 'right', style: 'width: 140px' },
        { name: 'add_qty', label: 'Кол-во', align: 'center', style: 'width: 110px' },
        { name: 'actions', label: '', align: 'center', style: 'width: 50px' },
      ],
    };
  },
  computed: {
    invoice() {
      return this.invoiceData || this.data.invoice;
    },
    isMultiRequest() {
      const ids = new Set(this.items.map(i => i.provider_request_id).filter(Boolean));
      return ids.size > 1;
    },
    tableRows() {
      if (!this.isMultiRequest) {
        return this.items.map(i => ({ ...i, _rowKey: i.provider_request_invoice_item_id }));
      }
      const groups = {};
      for (const item of this.items) {
        const rid = item.provider_request_id || 0;
        if (!groups[rid]) groups[rid] = [];
        groups[rid].push(item);
      }
      const result = [];
      for (const rid of Object.keys(groups)) {
        result.push({
          _isGroupHeader: true,
          _requestId: rid,
          _itemsCount: groups[rid].length,
          _rowKey: `group_${rid}`,
        });
        for (const item of groups[rid]) {
          result.push({ ...item, _rowKey: item.provider_request_invoice_item_id });
        }
      }
      return result;
    },
    totalSum() {
      if (this.editMode) {
        return this.items.reduce((sum, item) => {
          const qty = this.editItems[item.provider_request_invoice_item_id] || 0;
          const price = this.getEditPrice(item);
          return sum + qty * price;
        }, 0);
      }
      return this.items.reduce((sum, item) => sum + (item.item_sum || 0), 0);
    },
    isManualSigned() {
      return Boolean(this.invoice?.is_manual_signed);
    },
    isPendingApproval() {
      return Boolean(this.invoice?.is_pending_approval);
    },
    isRejectedApproval() {
      return Boolean(this.invoice?.is_rejected_approval);
    },
    isApprovedApproval() {
      return Boolean(this.invoice?.is_approved_approval);
    },
    canSign() {
      if (this.isManualSigned) return false;
      return this.invoice?.sign_info?.can_sign === true;
    },
    canDelete() {
      if (this.isManualSigned) return false;
      // Можно удалить если нет подписей (sign_signed = 0 или нет sign_info)
      const signInfo = this.invoice?.sign_info;
      if (!signInfo) return true;
      return (signInfo.sign_signed || 0) === 0 && !signInfo.is_partially_signed;
    },
    canEdit() {
      if (this.isManualSigned) return false;
      return this.canDelete;
    },
    showActions() {
      return this.data?.showActions !== false && !!this.data?.showActions;
    },
    canSaveEdit() {
      if (!this.editForm.invoice_num || !this.editForm.invoice_date) return false;
      for (const item of this.items) {
        const id = item.provider_request_invoice_item_id;
        const qty = this.editItems[id];
        if (qty <= 0) return false;
        if (qty > item.max_qty) return false;
        const p = Number(this.editPrices[id]);
        if (!Number.isFinite(p) || p < 0) return false;
      }
      return true;
    },
  },
  methods: {
    normalizeText(value) {
      return (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
    },
    isProviderNameIdentical(row) {
      const a = this.normalizeText(row?.material_name);
      const b = this.normalizeText(row?.provider_material_name);
      return !!a && !!b && a === b;
    },
    getInvoiceId() {
      return this.data.invoice?.id || this.data.invoice?.provider_request_invoice_id;
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      // Формат YYYY-MM-DD
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-');
        return `${day}.${month}.${year}`;
      }
      // Формат DD.MM.YYYY
      if (typeof dateStr === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
        return dateStr;
      }
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleDateString('ru-RU');
    },
    formatAmount(value) {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value) + ' ₸';
    },
    getSignStatusLabel(signInfo) {
      if (!signInfo) return 'Нет данных';
      if (signInfo.is_fully_signed) return 'Полностью подписано';
      if (signInfo.is_partially_signed) return 'Частично подписано';
      if (signInfo.sign_signed > 0) return 'В процессе';
      return 'Не подписано';
    },
    getSignStatusColor(signInfo) {
      if (!signInfo) return 'grey';
      if (signInfo.is_fully_signed) return 'green';
      if (signInfo.is_partially_signed) return 'blue';
      if (signInfo.sign_signed > 0) return 'blue';
      return 'grey';
    },
    getSignatureStatusLabel(status) {
      const labels = {
        'SIGNED': 'Подписано',
        'NOT_STARTED': 'Ожидает',
        'IN_PROGRESS': 'В процессе',
        'REJECTED': 'Отклонено',
        'EXPIRED': 'Просрочено',
      };
      return labels[status] || status || '-';
    },
    getSignatureStatusColor(status) {
      const colors = {
        'SIGNED': 'green',
        'NOT_STARTED': 'grey',
        'IN_PROGRESS': 'blue',
        'REJECTED': 'red',
        'EXPIRED': 'orange',
      };
      return colors[status] || 'grey';
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleString('ru-RU', { 
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },
    getSendTo1cLabel(isSendTo1c) {
      const n = Number(isSendTo1c);
      if (n === 2) return 'Отправлен в 1С';
      if (n === 1) return 'Готов к отправке';
      return 'Не отправлен';
    },
    getSendTo1cBadgeColor(isSendTo1c) {
      const n = Number(isSendTo1c);
      if (n === 2) return 'green';
      if (n === 1) return 'amber';
      return 'grey';
    },
    async loadData() {
      const invoiceId = this.getInvoiceId();
      if (!invoiceId) return;
      
      this.loading = true;
      
      // Загружаем данные накладной и позиции параллельно
      const [invoiceRes, itemsRes] = await Promise.all([
        getInvoice(invoiceId),
        getInvoiceItems(invoiceId)
      ]);
      
      this.invoiceData = invoiceRes;
      this.items = itemsRes;
      this.loading = false;
    },
    parseFilenameFromDisposition(disposition) {
      if (!disposition) return null;
      const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
      return match ? match[1] : null;
    },
    async downloadInvoicePdf() {
      const invoiceId = this.getInvoiceId();
      if (!invoiceId) return;

      this.downloading = true;
      try {
        const response = await downloadInvoice(invoiceId);
        const contentType = response?.headers?.['content-type'] || 'application/pdf';
        const disposition = response?.headers?.['content-disposition'];
        const filename = this.parseFilenameFromDisposition(disposition) || `Накладная_${invoiceId}.pdf`;

        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$q.notify({
          type: 'negative',
          message: e?.response?.data?.error || 'Ошибка формирования накладной',
        });
      } finally {
        this.downloading = false;
      }
    },
    async downloadInvoiceScanFile() {
      const invoiceId = this.getInvoiceId();
      if (!invoiceId) return;

      this.downloading = true;
      try {
        const response = await downloadInvoiceScan(invoiceId);
        const contentType = response?.headers?.['content-type'] || 'application/pdf';
        const disposition = response?.headers?.['content-disposition'];
        const filename = this.parseFilenameFromDisposition(disposition) || `Накладная_${invoiceId}_скан.pdf`;

        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$q.notify({
          type: 'negative',
          message: e?.response?.data?.error || 'Ошибка скачивания скана накладной',
        });
      } finally {
        this.downloading = false;
      }
    },
    async signInvoice() {
      const invoiceId = this.getInvoiceId();
      if (!invoiceId) return;

      this.signing = true;
      try {
        const res = await signInvoice(invoiceId);
        const signUrl = res?.sign_url;

        if (!signUrl) {
          this.$q.notify({
            type: 'negative',
            message: 'Сервис подписи не вернул ссылку для подписания',
          });
          return;
        }

        // Переход на страницу подписания NCALayer
        window.location.href = signUrl;
      } catch (e) {
        this.$q.notify({
          type: 'negative',
          message: e?.response?.data?.error || 'Ошибка при создании процесса подписания',
        });
      } finally {
        this.signing = false;
      }
    },
    confirmDelete() {
      this.$q.dialog({
        title: 'Удаление накладной',
        message: `Вы уверены, что хотите удалить накладную №${this.invoice?.num || this.invoice?.invoice_num}?`,
        cancel: true,
        persistent: true,
        ok: {
          label: 'Удалить',
          color: 'red',
          flat: true
        },
        cancel: {
          label: 'Отмена',
          flat: true
        }
      }).onOk(() => {
        this.doDelete();
      });
    },
    async doDelete() {
      const invoiceId = this.getInvoiceId();
      if (!invoiceId) return;

      this.deleting = true;
      try {
        await deleteInvoice(invoiceId);
        this.$q.notify({
          type: 'positive',
          message: 'Накладная успешно удалена',
        });
        // Сначала закрываем модалку
        this.closeModal();
        // Вызываем callback если есть (после закрытия)
        if (this.data.onDeleted) {
          await this.data.onDeleted();
        }
      } catch (e) {
        const errorMsg = e?.response?.data?.error || e?.response?.data?.message || 'Ошибка при удалении накладной';
        this.$q.notify({
          type: 'negative',
          message: errorMsg,
        });
      } finally {
        this.deleting = false;
      }
    },
    formatDateForInput(dateStr) {
      if (!dateStr) return '';
      // Если уже в формате YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      // Если в формате DD.MM.YYYY
      const parts = dateStr.split('.');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      // Пробуем как Date
      const d = new Date(dateStr);
      if (!isNaN(d)) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
      return '';
    },
    async startEdit() {
      // Заполняем форму текущими данными
      this.editForm.invoice_num = this.invoice?.num || this.invoice?.invoice_num || '';
      this.editForm.invoice_date = this.formatDateForInput(this.invoice?.date || this.invoice?.invoice_date);
      this.editForm.is_nds = Boolean(this.invoice?.is_nds);
      this.editForm.power_of_attorney_num = this.invoice?.power_of_attorney_num || '';
      this.editForm.power_of_attorney_date = this.formatDateForInput(this.invoice?.power_of_attorney_date);
      this.editForm.power_of_attorney_issued_to = this.invoice?.power_of_attorney_issued_to || '';
      // Заполняем количества для редактирования
      this.editItems = {};
      this.editPrices = {};
      this.items.forEach(item => {
        const pid = item.provider_request_invoice_item_id;
        this.editItems[pid] = item.material_cnt;
        this.editPrices[pid] = Number(item.material_price) || 0;
      });
      // Загружаем доступные позиции для добавления
      this.addItemQty = {};
      this.addItemPrice = {};
      this.availableItems = await getAvailableItemsForInvoice(this.getInvoiceId());
      this.availableItems.forEach(item => {
        const id = item.provider_request_item_id;
        this.addItemPrice[id] = item.is_show_sum_price ? (Number(item.material_price) || null) : null;
      });
      this.editMode = true;
    },
    cancelEdit() {
      this.editMode = false;
      this.editItems = {};
      this.editPrices = {};
      this.availableItems = [];
      this.addItemQty = {};
      this.addItemPrice = {};
    },
    getEditPrice(item) {
      const id = item?.provider_request_invoice_item_id;
      if (!id && id !== 0) return 0;
      const v = this.editPrices[id];
      if (v !== undefined && v !== null && Number.isFinite(Number(v))) {
        return Number(v);
      }
      return Number(item.material_price) || 0;
    },
    getEditItemSum(item) {
      const qty = this.editItems[item.provider_request_invoice_item_id] || 0;
      return qty * this.getEditPrice(item);
    },
    recalcItemSum(item) {
      // Пересчет суммы происходит автоматически через computed
    },
    confirmDeleteItem(item) {
      this.$q.dialog({
        title: 'Удаление позиции',
        message: `Удалить "${item.material_name}" из накладной?`,
        cancel: true,
        persistent: true,
        ok: {
          label: 'Удалить',
          color: 'red',
          flat: true
        },
        cancel: {
          label: 'Отмена',
          flat: true
        }
      }).onOk(() => {
        this.doDeleteItem(item);
      });
    },
    async doDeleteItem(item) {
      try {
        await deleteInvoiceItem(item.provider_request_invoice_item_id);
        this.$q.notify({
          type: 'positive',
          message: 'Позиция удалена',
        });
        // Удаляем из локального списка и editItems
        this.items = this.items.filter(i => i.provider_request_invoice_item_id !== item.provider_request_invoice_item_id);
        delete this.editItems[item.provider_request_invoice_item_id];
        delete this.editPrices[item.provider_request_invoice_item_id];
        // Обновляем список доступных позиций
        this.availableItems = await getAvailableItemsForInvoice(this.getInvoiceId());
        // Вызываем callback
        if (this.data.onDeleted) {
          this.data.onDeleted();
        }
      } catch (e) {
        const errorMsg = e?.response?.data?.error || e?.response?.data?.message || 'Ошибка при удалении позиции';
        this.$q.notify({
          type: 'negative',
          message: errorMsg,
        });
      }
    },
    async doAddItem(item) {
      const qty = this.addItemQty[item.provider_request_item_id];
      const price = this.addItemPrice[item.provider_request_item_id];
      if (!(qty > 0) || !(price > 0)) return;

      try {
        await addInvoiceItem(this.getInvoiceId(), item.provider_request_item_id, qty, price);
        this.$q.notify({
          type: 'positive',
          message: 'Позиция добавлена',
        });
        // Перезагружаем позиции и доступные
        const invoiceId = this.getInvoiceId();
        const [itemsRes, availableRes] = await Promise.all([
          getInvoiceItems(invoiceId),
          getAvailableItemsForInvoice(invoiceId)
        ]);
        this.items = itemsRes;
        this.availableItems = availableRes;
        // Обновляем editItems для новой позиции
        this.items.forEach(i => {
          const pid = i.provider_request_invoice_item_id;
          if (!(pid in this.editItems)) this.editItems[pid] = i.material_cnt;
          if (!(pid in this.editPrices)) this.editPrices[pid] = Number(i.material_price) || 0;
        });
        // Очищаем поле ввода
        delete this.addItemQty[item.provider_request_item_id];
        delete this.addItemPrice[item.provider_request_item_id];
        // Вызываем callback
        if (this.data.onDeleted) {
          this.data.onDeleted();
        }
      } catch (e) {
        const errorMsg = e?.response?.data?.error || e?.response?.data?.message || 'Ошибка при добавлении позиции';
        this.$q.notify({
          type: 'negative',
          message: errorMsg,
        });
      }
    },
    async saveEdit() {
      const invoiceId = this.getInvoiceId();
      if (!invoiceId || !this.canSaveEdit) return;

      this.saving = true;
      try {
        // Сначала обновляем количества в позициях
        for (const item of this.items) {
          const id = item.provider_request_invoice_item_id;
          const newQty = this.editItems[id];
          const newPriceNum = Number(this.editPrices[id]);
          const oldPriceNum = Number(item.material_price) || 0;
          const qtyChanged = newQty !== item.material_cnt;
          const priceChanged = Number.isFinite(newPriceNum) && Math.abs(newPriceNum - oldPriceNum) > 1e-6;
          if (qtyChanged || priceChanged) {
            await updateInvoiceItemQty(
              id,
              newQty,
              priceChanged ? newPriceNum : undefined,
            );
          }
        }
        // Затем обновляем данные накладной
        await updateInvoice(invoiceId, {
          invoice_num: this.editForm.invoice_num,
          invoice_date: this.editForm.invoice_date,
          is_nds: this.editForm.is_nds ? 1 : 0,
          power_of_attorney_num: (this.editForm.power_of_attorney_num || '').trim() || null,
          power_of_attorney_date: this.editForm.power_of_attorney_date || null,
          power_of_attorney_issued_to: (this.editForm.power_of_attorney_issued_to || '').trim() || null,
        });
        this.$q.notify({
          type: 'positive',
          message: 'Накладная успешно обновлена',
        });
        this.editMode = false;
        this.editItems = {};
        this.editPrices = {};
        // Перезагружаем данные
        await this.loadData();
        // Вызываем callback если есть
        if (this.data.onUpdated) {
          this.data.onUpdated();
        }
        if (this.data.onDeleted) {
          // Используем onDeleted как общий callback для обновления списка
          this.data.onDeleted();
        }
      } catch (e) {
        const errorMsg = e?.response?.data?.error || e?.response?.data?.message || 'Ошибка при обновлении накладной';
        this.$q.notify({
          type: 'negative',
          message: errorMsg,
        });
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped lang="scss">
.view-invoice-modal {
  min-width: 500px;
  max-width: 700px;
  padding: 20px;
}

.invoice-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  margin-bottom: 20px;
}

.info-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.label {
  color: #6c757d;
  font-size: 13px;
}

.amount {
  color: #2e7d32;
  font-size: 16px;
}

.info-row--onec {
  grid-column: 1 / -1;
}

.onec-line {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.onec-kod {
  font-size: 13px;
  color: #424242;
}

.items-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;

  h4 {
    margin: 0;
    padding: 12px 16px;
    background: #f5f5f5;
    font-size: 14px;
    font-weight: 600;
  }
}

.items-table {
  max-height: 360px;
  overflow-y: auto;

  :deep(td) {
    vertical-align: middle !important;
  }
}

.material-info {
  max-width: 300px;
  white-space: normal;
  line-height: 1.3;
}

.material-title {
  font-size: 13px;
}

.provider-meta {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.provider-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.provider-block--identical {
  background: #e8f5e9;
  border: 1px solid #c8e6c9;
  border-left: 4px solid #2e7d32;
  border-radius: 6px;
  padding: 6px 8px;
}

.provider-head {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.provider-label {
  color: #9e9e9e;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

.provider-value {
  color: #455a64;
  font-size: 12px;
  line-height: 1.25;
}

.provider-value--provider {
  color: #1565c0;
  font-weight: 600;
}

.provider-badge {
  font-size: 10px;
  padding: 2px 6px;
}

.group-header-row {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important;
  border-top: 2px solid #1976d2;
  position: sticky;
  top: 0;
  z-index: 1;
}

.group-header-cell {
  padding: 10px 16px !important;
}

.group-header-content {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #1565c0;
}

.unit-name {
  font-size: 11px;
  color: #6c757d;
  margin-left: 4px;
}

.edit-input-wrap {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.unit-name--edit {
  margin-top: 7px;
}

.edit-hint {
  font-size: 11px;
  color: #9e9e9e;
  margin-top: 2px;
}

.edit-qty-cell {
  white-space: nowrap;
}

.edit-price-cell {
  white-space: nowrap;
}

.no-items {
  padding: 20px;
  text-align: center;
  color: #9e9e9e;
}

.totals-section {
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 8px;
}

.total-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.total-sum {
  color: #2e7d32;
  font-size: 16px;
}

.manual-banner,
.approval-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  margin-bottom: 16px;
  padding: 10px 14px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-left: 4px solid #ff8f00;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #6d4c00;
}

.approved-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  margin-bottom: 16px;
  padding: 10px 14px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-left: 4px solid #2e7d32;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1b5e20;
}

.approved-banner__icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.approved-banner__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.approved-banner__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 4px;
  border-top: 1px solid #a5d6a7;
}

.approved-banner__who {
  font-size: 12px;
  font-weight: 700;
  color: #1b5e20;
}

.approved-banner__comment {
  font-size: 12px;
  font-weight: 400;
  color: #2e7d32;
  white-space: pre-wrap;
}

.rejected-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 12px;
  margin-bottom: 16px;
  padding: 10px 14px;
  background: #ffebee;
  border: 1px solid #ef9a9a;
  border-left: 4px solid #c62828;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #b71c1c;
}

.rejected-banner__icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.rejected-banner__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rejected-banner__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 4px;
  border-top: 1px solid #ef9a9a;
}

.rejected-banner__who {
  font-size: 12px;
  font-weight: 700;
  color: #7f0000;
}

.rejected-banner__comment {
  font-size: 12px;
  font-weight: 400;
  color: #c62828;
  white-space: pre-wrap;
}

.sign-info-section {
  margin-top: 16px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.sign-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.sign-title {
  font-weight: 600;
  font-size: 14px;
  color: #424242;
}

.sign-progress {
  font-size: 12px;
  color: #757575;
  margin-left: auto;
}

.sign-progress-bar {
  margin-bottom: 12px;
  border-radius: 4px;
}

.signatures-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.signature-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px;
  background: white;
  border-radius: 6px;
  border: 1px solid #eeeeee;
}

.signature-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.signature-desc {
  font-size: 13px;
  color: #424242;
}

.signature-signer {
  font-size: 12px;
  color: #1976d2;
  font-weight: 500;
}

.signature-date {
  font-size: 11px;
  color: #9e9e9e;
}

.next-to-sign {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e0e0e0;
  font-size: 12px;
  color: #1976d2;
}

.invoice-edit-form {
  padding: 16px;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 8px;
  margin-bottom: 20px;
}

.poa-section {
  margin-top: 12px;
  margin-bottom: 20px;
}

.poa-expansion {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
}

.poa-fields {
  padding: 12px 16px 14px 16px;
}

.poa-view {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.poa-view-row {
  display: flex;
  gap: 10px;
  align-items: baseline;
}

.poa-label {
  color: #9e9e9e;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  min-width: 90px;
}

.poa-value {
  color: #455a64;
  font-size: 12px;
}

.poa-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 12px;
}

.poa-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.poa-field--num {
  flex: 1;
  min-width: 240px;
}

.poa-field--date {
  width: 180px;
  min-width: 180px;
}

.form-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
}

.add-items-section {
  margin-top: 16px;
  border: 1px solid #c8e6c9;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f8e9;
}

.add-items-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #dcedc8;

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #33691e;
  }
}

.add-items-table {
  background: white;
}

.remaining {
  color: #2e7d32;
  font-weight: 600;
}
</style>

